import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Concatène des classes Tailwind en gérant les conflits
 * (utilitaire standard de shadcn/ui).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/* -------------------------------------------------------------------------- */
/* Formatters français (source unique — P2.4 audit)                            */
/* -------------------------------------------------------------------------- */
/*
 * Point de vérité des formats d'affichage FR, factorisés depuis ≈6 composants
 * qui les réimplémentaient (et divergeaient). On préserve les **chaînes rendues**
 * telles quelles ; deux familles kWh coexistent volontairement :
 *   - ``formatKwh``        : 1 décimale, non arrondi   → « 1 234,5 kWh » (chart)
 *   - ``formatKwhRounded`` : arrondi à l'entier        → « 1 235 kWh »   (hero/liste/funnel)
 * NB : le pourcentage FR affiché partout est « 62,0 % » (1 décimale forcée), et
 * NON le « 62 % » de l'ancien ``Intl`` style:"percent" (supprimé, jamais utilisé).
 */

// maxFrac 1 / minFrac 0 → « 1 234,5 » (kWh non arrondi + ticks d'axe).
const energyFormatter = new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

// Entier FR (kWh/€ arrondis) → « 1 235 ».
const integerFormatter = new Intl.NumberFormat("fr-FR", {
  maximumFractionDigits: 0,
});

/** Énergie kWh **non arrondie** (1 décimale FR) : 1234.5 → « 1 234,5 kWh ». */
export function formatKwh(value: number): string {
  return `${energyFormatter.format(value)} kWh`;
}

/** Énergie kWh **arrondie** à l'entier (hero / liste / funnel) : 1234.5 → « 1 235 kWh ». */
export function formatKwhRounded(value: number): string {
  return `${integerFormatter.format(Math.round(value))} kWh`;
}

/** Montant € **arrondi** à l'entier : 1234.5 → « 1 235 € ». */
export function formatEurRounded(value: number): string {
  return `${integerFormatter.format(Math.round(value))} €`;
}

/** Valeur **déjà en %** (0..100) → pourcentage FR **1 décimale** (virgule) : 62 → « 62,0 % ». */
export function formatPercentValue(pct: number): string {
  return `${pct.toFixed(1).replace(".", ",")} %`;
}

/** Taux backend 0..1 → pourcentage FR **1 décimale** (virgule) : 0.62 → « 62,0 % ». */
export function formatRatePercent(rate: number): string {
  return formatPercentValue(rate * 100);
}

/** Nombre FR générique **sans unité** (défaut 1 décimale) : 5.5 → « 5,5 ». */
export function formatNumber(value: number, maximumFractionDigits = 1): string {
  return value.toLocaleString("fr-FR", { maximumFractionDigits });
}
